import {
  combineUnits,
  determineAppropriateUnit,
  getUnitOrFallback,
  searchForUnit,
  stringToNumber,
  toAbsoluteAmount,
  toRelativeAmount,
} from "@/lib/measurement";
import { RecipeTemplate } from "@/lib/types";
import { UploadedRecipesObject } from "@/pages/api/recipe-annotation-to-template";
import cuid from "cuid";
import {
  AllRecipesQuery,
  IngredientQuery,
  IngredientsDocument,
  IngredientsQuery,
  IngredientsQueryVariables,
  SearchIngredientsDocument,
  SearchIngredientsQuery,
  SearchIngredientsQueryVariables,
} from "@/queries/autogenerate/graphql";
import { Dispatch, SetStateAction } from "react";
import { compareTwoStrings } from "string-similarity";
import { Client } from "urql";
import { z } from "zod";
import { CurrentUser } from "../Page";
import { RecipeIngredientSection } from "./CreateRecipeManually";
import { PartialRecipe } from "./FinalisePartialRecipe";
import { searchInventory } from "@/lib/util";

// Mappings from symbols etc. to numbers
export const numberMap = [
  { from: "½", to: "1/2" },
  { from: "⅓", to: "1/3" },
  { from: "¼", to: "1/4" },
  { from: "¾", to: "3/4" },
  { from: "⅔", to: "2/3" },
  { from: "⅛", to: "1/8" },
  { from: "⅜", to: "3/8" },
  { from: "⅝", to: "5/8" },
  { from: "⅞", to: "7/8" },
  { from: "⅕", to: "1/5" },
  { from: "⅖", to: "2/5" },
  { from: "⅗", to: "3/5" },
  { from: "⅘", to: "4/5" },
  { from: "⅙", to: "1/6" },
  { from: "⅚", to: "5/6" },
  // { from: "1/2", to: ".5" },
  // { from: "1/3", to: ".3" },
  // { from: "2/3", to: ".6" },
  // { from: "1/4", to: ".25" },
  // { from: "3/4", to: ".75" },
];
// Mappings from abbreviations to full units
export const unitMap = {
  teaspoons: "Teaspoons",
  teaspoon: "Teaspoons",
  tsp: "Teaspoons",
  ts: "Teaspoons",
  t: "Teaspoons",
  // --
  tablespoons: "Tablespoons",
  tablespoon: "Tablespoons",
  tbsp: "Tablespoons",
  tbs: "Tablespoons",
  tb: "Tablespoons",
  T: "Tablespoons",
  // --
  millilitres: "Millilitres",
  millilitre: "Millilitres",
  ml: "Millilitres",
  // --
  grams: "Grams",
  gram: "Grams",
  gm: "Grams",
  g: "Grams",
  // --
  kilograms: "Kilograms",
  kilogram: "Kilograms",
  kg: "Kilograms",
  // --
  cups: "Cups",
  cup: "Cups",
  c: "Cups",
};

export const textToIngredient: (
  user: CurrentUser,
  ingredients: IngredientsQuery["ingredients"],
  text: string,
  specifics?: {
    name?: string;
    amount?: string;
    unit?: string;
    preparation?: string | null;
  }
) => Promise<
  PartialRecipe["ingredientSections"][number]["ingredients"][number]
> = async (user, ingredients, initialText, specifics = {}) => {
  let name = "";
  let amount = "1";
  let unitText = "";
  let preparation = specifics.preparation || "";

  if (specifics.name) {
    name = specifics.name;
  } else {
    name = initialText.replace(/^- /, "");
  }

  const unmappedText = numberMap.reduce(
    (prev, { from, to }) => prev.replace(from, to),
    initialText
  );
  const amountRegEx =
    /^((\d+\.?\d*)|(\d*\.?\d+)|(\d+(\/\d+)?)|((\d+ )\d+\/\d+)) *\w+/g;
  const amountTextMatch = unmappedText.match(amountRegEx);
  const amountText = amountTextMatch ? amountTextMatch[0] : "";

  if (specifics.amount) {
    amount = numberMap.reduce(
      (prev, { from, to }) => prev.replace(from, to),
      specifics.amount
    );
  } else {
    // Find the amount portion of match
    const amountMatch = amountText.match(
      /((\d+\.?\d*)|(\d*\.?\d+)|(\d+(\/\d+)?)|((\d+ )\d+\/\d+))/g
    );
    amount = amountMatch ? amountMatch[0].replace(" ", "") : "1";
  }

  if (specifics.unit) {
    unitText =
      unitMap[
        specifics.unit === "T" ? specifics.unit : specifics.unit.toLowerCase()
      ] || specifics.unit;
  } else {
    // Find the unit portion of the text
    const unitSubstring = amountText.match(/[A-z]+/g);
    const str = unitSubstring && unitSubstring[0];
    if (str && unitMap.hasOwnProperty(str.toLowerCase())) {
      unitText = unitMap[str === "T" ? str : str.toLowerCase()];
    }
  }

  const ingredientOptions = getIngredientOptions(
    name.replace(amountRegEx, ""),
    ingredients
  );

  const [highestMatch] = ingredientOptions;
  const highestValidMatch =
    highestMatch && highestMatch.similarity >= 60 ? highestMatch : null;
  const ingredientObject = {
    uuid: cuid(),
    text: initialText.replace(/^- /, ""),
    name: name.replace(amountRegEx, ""),
    amount,
    preparation,
    unitId: highestValidMatch
      ? searchForUnit(
          combineUnits(highestValidMatch),
          unitText,
          highestValidMatch.density ?? undefined,
          highestValidMatch.selectionUnit ?? undefined
        )?.id
      : undefined,
    measurement: {
      label: "Discrete Units",
      value: null,
    },
    derivedUnit: unitText, // unit derived from the data
    isChecked: false, // for density
    density: 845,
    tags: [],
    selected: highestValidMatch || undefined,
    options: [...ingredientOptions],
  };

  return ingredientObject;
  // recipe.ingredients.push(ingredientObject);
};

export const getIngredientOptions = (
  text: string,
  ingredients: IngredientsQuery["ingredients"]
) => {
  const searchedIngredients = searchInventory(text, ingredients);

  const matchingIngredients =
    searchedIngredients
      .map((i) => ({
        ...i,
        id: i.id,
        similar: true,
        similarity: Math.round(
          compareTwoStrings(i.name.toLowerCase(), text.toLowerCase()) * 100
        ),
      }))
      .sort((a, b) => b.similarity - a.similarity)
      .slice(0, 5) || [];

  return matchingIngredients;
};

export const recipeTemplatesToString = (templates: RecipeTemplate[]) => {
  let templateString = "";

  templates.forEach((template) => {
    let recipeString = "<--recipe-->\n";

    // add name
    recipeString += `===\nname: ${template.name}\n===\n`;

    // add servings
    recipeString += `===\nservings: ${template.servings}\n===\n`;

    // add source (if exists)
    if (template.source) {
      recipeString += `===\nsource: ${template.source}\n===\n`;
    }

    // add method
    recipeString += `===\n<--method-->\n${template.method}\n<--/method-->\n===\n`;

    // add ingredients
    recipeString += `===\n<--ingredient-sections-->\n`;
    template.ingredientSections.forEach((section) => {
      recipeString += `<--section-->\n`;
      if (typeof section.name === "string") {
        recipeString += `---\nname: ${section.name}\n---\n`;
      }

      recipeString += `ingredients:\n`;
      section.ingredients.forEach((ingredient) => {
        recipeString += `<--ingredient-->\ntext: ${ingredient.text}\nname: ${ingredient.name}\namount: ${ingredient.amount}\nunit: ${ingredient.unit}\npreparation: ${ingredient.preparation}\n<--/ingredient-->\n`;
      });
      recipeString += `<--/section-->\n`;
    });
    recipeString += `<--/ingredient-sections-->\n===\n`;

    // add information (if exists)
    if (template.information.length > 0) {
      recipeString += `===\n<--information-->\n`;
      template.information.forEach((info) => {
        recipeString += `<--->\ninfo: ${info}\n<--->\n`;
      });
      recipeString += `<--/information-->\n===\n`;
    }

    recipeString += "<--/recipe-->\n";

    templateString += recipeString;
  });

  console.log(templateString);

  return templateString;
};

export async function templateStringToPartialRecipes(
  user: CurrentUser,
  ingredients: IngredientsQuery["ingredients"],
  templateString: string
): Promise<PartialRecipe[]> {
  console.log(templateString);

  let partialRecipes: PartialRecipe[] = [];

  const tempRecipes = templateString.split("<--recipe-->").filter((r) => r);
  for await (const tempRecipe of tempRecipes) {
    let partialRecipe: PartialRecipe = {
      uuid: cuid(),
      name: "",
      public: false,
      process: "document",
      servings: 0,
      source: "",
      method: "",
      ingredientSections: [],
      recipeInformation: [],
      tags: [],
    };

    // get name
    const nameMatch = tempRecipe.match(/===\nname: (.*)\n===/);
    if (nameMatch) {
      partialRecipe.name = nameMatch[1];
    }

    // get servings
    const servingsMatch = tempRecipe.match(/===\nservings: (.*)\n===/);
    if (servingsMatch) {
      partialRecipe.servings = Number(servingsMatch[1]);
    }

    // get source (if exists)
    const sourceMatch = tempRecipe.match(/===\nsource: (.*)\n===/);
    if (sourceMatch) {
      partialRecipe.source = sourceMatch[1];
    }

    // get method
    const methodMatch = tempRecipe.match(/<--method-->\n(.*)\n<--\/method-->/s);
    if (methodMatch) {
      partialRecipe.method = methodMatch[1];
    }

    // get ingredient sections
    const ingredientSectionMatches = tempRecipe.match(
      /<--ingredient-sections-->\n([\S\s]*)\n<--\/ingredient-sections-->/s
    );
    if (ingredientSectionMatches) {
      let ingredientSections: PartialRecipe["ingredientSections"] = [];

      // get sections
      const sectionGroups = ingredientSectionMatches[1]
        .split("<--section-->")
        .filter((s) => s)
        .map((section) => {
          return `<--section-->\n${section}`;
        });

      for await (const sectionGroup of sectionGroups) {
        let ingredientSection: PartialRecipe["ingredientSections"][0] = {
          uuid: cuid(),
          name: "",
          ingredients: [],
          section: false,
        };

        // get section name
        const sectionNameMatch = sectionGroup.match(/---\nname: (.*)\n---/);
        if (sectionNameMatch?.[1].trim()) {
          ingredientSection.name = sectionNameMatch[1];
          ingredientSection.section = true;
        } else {
          ingredientSection.name = "Default Section";
        }

        // get ingredients
        const broadIngredientMatches = sectionGroup.match(
          /<--ingredient-->\n([\S\s]*)\n<--\/ingredient-->/s
        );
        if (broadIngredientMatches) {
          const ingredientGroups = broadIngredientMatches[1]
            .split("<--ingredient-->")
            .filter((i) => i)
            .map((ingredient) => {
              return `<--ingredient-->\n${ingredient}`;
            });

          for await (const ingredientGroup of ingredientGroups) {
            let derivedText = "";

            let derivedName = "";
            let derivedAmount = "";
            let derivedUnit = "";
            let derivedPreparation = "";

            // get text at once, if exists
            const textMatch = ingredientGroup.match(/text: (.*)/);
            if (textMatch) {
              derivedText = textMatch[1];
            }

            // get name
            const nameMatch = ingredientGroup.match(/name: (.*)\n/);
            if (nameMatch) {
              derivedName = nameMatch[1];
            }

            // get amount
            const amountMatch = ingredientGroup.match(/amount: (.*)\n/);
            if (amountMatch) {
              derivedAmount = amountMatch[1];
            }

            // get unit
            const unitMatch = ingredientGroup.match(/unit: (.*)\n/);
            if (unitMatch) {
              derivedUnit = unitMatch[1];
            }

            // get preparation
            const preparationMatch =
              ingredientGroup.match(/preparation: (.*)\n/);
            if (
              preparationMatch?.[1] &&
              preparationMatch[1].trim() !== "None"
            ) {
              derivedPreparation = preparationMatch[1];
            }

            let ingredient: PartialRecipe["ingredientSections"][0]["ingredients"][0] =
              await textToIngredient(user, ingredients, derivedText, {
                name: derivedName,
                amount: derivedAmount,
                unit: derivedUnit,
                preparation: derivedPreparation,
              });

            ingredientSection.ingredients.push(ingredient);
          }
        }

        ingredientSections.push(ingredientSection);
      }

      partialRecipe.ingredientSections = ingredientSections;
    }

    // get information (if exists)
    const informationMatches = tempRecipe.match(
      /===\n<--information-->\n([\S\s]*)\n<--\/information-->\n===/s
    );
    if (informationMatches) {
      const informationGroups = informationMatches[1]
        .split("<--->")
        .filter((i) => i)
        .map((info) => {
          return `<--->\n${info}`;
        });

      informationGroups.forEach((informationGroup) => {
        const informationMatch = informationGroup.match(/info: (.*)\n/);
        if (informationMatch) {
          partialRecipe.recipeInformation?.push(informationMatch[1]);
        }
      });
    }

    partialRecipes.push(partialRecipe);
  }

  return partialRecipes;
}

export async function uploadedRecipesToPartialRecipes(
  user: CurrentUser,
  ingredients: IngredientsQuery["ingredients"],
  uploadedRecipes: z.infer<typeof UploadedRecipesObject>
): Promise<PartialRecipe[]> {
  let partialRecipes: PartialRecipe[] = [];

  for await (const uploadedRecipe of uploadedRecipes.recipes) {
    let partialRecipe: PartialRecipe = {
      uuid: cuid(),
      name: uploadedRecipe.name,
      public: false,
      process: "document",
      servings: Number(uploadedRecipe.servings),
      source: uploadedRecipe.source,
      method: uploadedRecipe.method,
      ingredientSections: [],
      recipeInformation: [],
      tags: [],
    };

    console.group("Uploaded Recipe", uploadedRecipe.name);

    // get ingredient sections
    for (const section of uploadedRecipe.ingredientSections) {
      let ingredientSection: PartialRecipe["ingredientSections"][0] = {
        uuid: cuid(),
        name: section.name || "Default Section",
        ingredients: [],
        section: section.name ? true : false,
      };

      console.group("Section", section.name);

      for (const ingredient of section.ingredients) {
        console.log("Ingredient", ingredient.text);

        let derivedText = ingredient.text;

        let derivedName = ingredient.name;
        let derivedAmount = ingredient.amount;
        let derivedUnit = ingredient.unit;
        let derivedPreparation = ingredient.preparation;

        let newIngredient: PartialRecipe["ingredientSections"][0]["ingredients"][0] =
          await textToIngredient(user, ingredients, derivedText, {
            name: derivedName,
            amount: derivedAmount,
            unit: derivedUnit,
            preparation: derivedPreparation,
          });

        ingredientSection.ingredients.push(newIngredient);
      }

      console.groupEnd();

      partialRecipe.ingredientSections.push(ingredientSection);
    }

    console.groupEnd();

    partialRecipes.push(partialRecipe);
  }

  console.log(partialRecipes);

  return partialRecipes;
}

// drag and drop functions - sections and ingredients
export function reorder<T>(
  ingredients: T[],
  startIndex: number,
  endIndex: number
) {
  const result = [...ingredients];
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
}

export function inlineConversion(
  amount: string | number,
  unit: NonNullable<
    NonNullable<IngredientQuery["ingredient"]>["measurement"]
  >["units"][0],
  combinedUnits: NonNullable<
    NonNullable<IngredientQuery["ingredient"]>["measurement"]
  >["units"],
  density: number
) {
  const decimalAmount = stringToNumber(String(amount));

  const absoluteAmount = toAbsoluteAmount(decimalAmount, unit, density);

  const tempUnit = determineAppropriateUnit(
    combinedUnits.filter(
      (u) =>
        u.measurementId &&
        (unit.isMass || !unit.measurementId ? !u.isMass : u.isMass)
    ),
    combinedUnits.find((u) => u.id == "cjos1kt0b3c320a167ylt6yvs"),
    absoluteAmount,
    density,
    false
  );

  return tempUnit
    ? `${decimalAmount} ${unit.name} = ${toRelativeAmount(
        absoluteAmount,
        tempUnit,
        density
      )} ${tempUnit.name}`
    : `${decimalAmount} ${unit.name}`;
}

export function loadManualRecipeIngredientsToStore(
  user: CurrentUser,
  client: Client,
  recipeIngredientSections: RecipeIngredientSection[],
  setIngredients: Dispatch<SetStateAction<IngredientsQuery["ingredients"]>>,
  counter: number,
  setCounter: Dispatch<SetStateAction<number>>
) {
  setCounter((c) => c + 1);

  if (
    counter < 2 &&
    recipeIngredientSections.some((ris) => ris.ingredients.length)
  ) {
    client
      .query<IngredientsQuery, IngredientsQueryVariables>(IngredientsDocument, {
        schoolID: user.school?.id || "",
        where: {
          id: {
            in: recipeIngredientSections
              .map((ris) => ris.ingredients.map((ri) => ri.id))
              .flat(),
          },
        },
      })
      .toPromise()
      .then(({ error, data }) => {
        if (error) return;
        if (!data) return;

        for (const newIngredient of data.ingredients) {
          setIngredients((ingredients) =>
            ingredients.find((i) => i.id === newIngredient.id)
              ? ingredients.map((i) =>
                  i.id === newIngredient.id ? newIngredient : i
                )
              : [...ingredients, newIngredient]
          );
        }
      });
  }
}

export function loadPartialRecipeIngredientsToStore(
  user: CurrentUser,
  client: Client,
  recipes: PartialRecipe[],
  setIngredients: Dispatch<SetStateAction<IngredientsQuery["ingredients"]>>,
  counter: number,
  setCounter: Dispatch<SetStateAction<number>>
) {
  setCounter((c) => c + 1);

  if (
    counter < 2 &&
    recipes?.some((r) =>
      r.ingredientSections.some((ris) => ris.ingredients.length)
    )
  ) {
    client
      .query<IngredientsQuery, IngredientsQueryVariables>(IngredientsDocument, {
        schoolID: user.school?.id || "",
        where: {
          id: {
            in: recipes
              .map((r) =>
                r.ingredientSections.map((ris) =>
                  ris.ingredients.map((ri) => ri.selected?.id)
                )
              )
              .flat(2)
              .filter((i): i is string => !!i),
          },
        },
      })
      .toPromise()
      .then(({ error, data }) => {
        if (error) return;
        if (!data) return;

        for (const newIngredient of data.ingredients) {
          setIngredients((ingredients) =>
            ingredients.find((i) => i.id === newIngredient.id)
              ? ingredients.map((i) =>
                  i.id === newIngredient.id ? newIngredient : i
                )
              : [...ingredients, newIngredient]
          );
        }
      });
  }
}

export const mapify: (
  user: CurrentUser,
  recipe: AllRecipesQuery["allRecipes"][number],
  ingredients: IngredientsQuery["ingredients"],
  client: Client
) => Promise<PartialRecipe[]> = async (user, recipe, ingredients, client) => {
  const tempSections: PartialRecipe["ingredientSections"] = await Promise.all(
    [null, ...recipe.recipeSections].map(async (section) => {
      const tempIngredients: PartialRecipe["ingredientSections"][number]["ingredients"] =
        await Promise.all(
          recipe.recipeIngredients
            .filter((i) =>
              section ? i.recipeSection?.id === section.id : !i.recipeSection
            )
            .map(
              async ({
                ingredient: { name, units: customUnits, measurement, density },
                amount: absoluteAmount,
                unit: defaultUnit,
              }) => {
                const units = combineUnits({
                  measurement,
                  units: customUnits,
                  density,
                });

                // unit
                const unit = getUnitOrFallback(units, defaultUnit);

                // amount
                const amount = toRelativeAmount(
                  absoluteAmount,
                  unit ?? undefined,
                  density ?? undefined,
                  recipe.servings
                );

                const text = `${name} - ${amount} ${unit?.name}`;

                const searchedIngredients = searchInventory(
                  name || text,
                  ingredients
                ).slice(0, 5);

                const matchingIngredients =
                  searchedIngredients
                    .map((i) => ({
                      ...i,
                      id: i.id,
                      similar: true,
                      similarity: Math.round(
                        compareTwoStrings(
                          i.name.toLowerCase(),
                          (name || text).toLowerCase()
                        ) * 100
                      ),
                    }))
                    .sort((a, b) => b.similarity - a.similarity) ?? [];

                const [highestMatch] = matchingIngredients;
                // const dd = await client.query(IngredientDocument, { where: { id: highestMatch.id } }).toPromise()
                const highestValidMatch =
                  highestMatch && highestMatch.similarity >= 60
                    ? highestMatch
                    : null;

                return {
                  uuid: cuid(),
                  text: text,
                  name: name,
                  amount: amount,
                  unitId: highestValidMatch
                    ? searchForUnit(
                        combineUnits(highestValidMatch),
                        unit?.name || "",
                        highestValidMatch.density ?? undefined,
                        highestValidMatch.selectionUnit ?? undefined
                      ).id
                    : undefined,
                  measurement: {
                    label: "Discrete Units",
                    value: null,
                  },
                  derivedUnit: unit?.name || "", // unit derived from the data
                  isChecked: false, // for density
                  density: 845,
                  tags: [],
                  selected: highestValidMatch || undefined,
                  options: [...matchingIngredients],
                };
              }
            )
        );

      return {
        uuid: cuid(),
        name: section ? section.name : "Default Section",
        section: !!section,
        ingredients: tempIngredients,
      };
    })
  );

  return [
    {
      uuid: cuid(),
      name: recipe.name,
      public: false,
      source: recipe.source ?? undefined,
      inspiration: recipe.id,
      process: "discovered",
      image: recipe.image ?? undefined,
      servings: recipe.servings,
      method: recipe.method ?? undefined,
      tags: [],
      ingredientSections: tempSections,
    },
  ];
};
